import gql from "graphql-tag";
import { EventEmitter } from "events";
import { hideGlobalLoader, showGlobalLoader } from "./loaderController";
import drivoApollo from "./drivoApollo";
import api from "./api";
import { getAuthHeaders } from "./authHeaders";

export const AUTH_STATE_CHANGE = "AUTH_STATE_CHANGE";
export const AUTHENTICATED = "AUTHENTICATED";
export const UN_AUTHENTICATED = "UN_AUTHENTICATED";

export const PERMISSIONS = {
	BOOKINGS_MANAGE: 'bookings:manage',
	BOOKINGS_READ: 'bookings:read',
	CAMERAS_READ: 'cameras:read',
	DASHBOARD_READ: 'dashboard:read',
	GUEST_CARDS_READ: 'guest_cards:read',
	GUEST_CARDS_MANAGE: 'guest_cards:manage',
	HOME_READ: 'home:read',
	INSIGHTS_OVERVIEW_READ: 'insights_overview:read',
	INSIGHTS_REVENUE_READ: 'insights_revenue:read',
	INSIGHTS_BUDGET_MANAGE: 'insights_budget:manage',
	INSIGHTS_STAFF_READ: 'insights_staff:read',
	INSIGHTS_NETWORK_READ: 'insights_network:read',
	INSIGHTS_OPERATIONS_READ: 'insights_operations:read',
	INSIGHTS_DATA_INPUT_MANAGE: 'insights_data_input:manage',
	PARKING_LOTS_READ: 'parking_lots:read',
	PARKING_LOTS_MANAGE: 'parking_lots:manage',
	PARKING_OPERATIONS_READ: 'parking_operations:read',
	PARKING_OPERATIONS_MANAGE: 'parking_operations:manage',
	PARKING_UPCOMING_READ: 'parking_upcoming:read',
	PAYOUTS_READ: 'payouts:read',
	PAYOUTS_MANAGE: 'payouts:manage',
	PEDESTRIAN_ACCESS_READ: 'pedestrian_access:read',
	REFUNDS_MANAGE: 'refunds:manage',
	REPORTS_EXPORT: 'reports:export',
	RUNNER_QUEUE_READ: 'runner_queue:read',
	RUNNER_QUEUE_MANAGE: 'runner_queue:manage',
	SANCTIONED_PLATES_MANAGE: 'sanctioned_plates:manage',
	SANCTIONED_PLATES_READ: 'sanctioned_plates:read',
	SANCTIONED_TIMES_READ: 'sanctioned_times:read',
	SANCTIONED_TIMES_MANAGE: 'sanctioned_times:manage',
	SHIFTS_READ: 'shifts:read',
	SHIFTS_MANAGE: 'shifts:manage',
	SUPPORT_READ: 'support:read',
	TRANSACTIONS_READ: 'transactions:read',
	USERS_MANAGE: 'users:manage',
	USERS_READ: 'users:read',
};

/**
 * Permissions only a root admin may put on a role — mirrors
 * ROOT_ONLY_ROLE_PERMISSIONS in the API (`api/src/roles/constants.ts`).
 * The API rejects these too; hiding them here just keeps the Roles form honest.
 */
export const ROOT_ONLY_PERMISSIONS = [
	PERMISSIONS.PEDESTRIAN_ACCESS_READ,
	PERMISSIONS.TRANSACTIONS_READ,
];

export const DEFAULT_ROLE_PERMISSIONS = [
	PERMISSIONS.DASHBOARD_READ,
	PERMISSIONS.HOME_READ,
	PERMISSIONS.BOOKINGS_READ,
	PERMISSIONS.BOOKINGS_MANAGE,
	PERMISSIONS.PARKING_UPCOMING_READ,
	PERMISSIONS.SUPPORT_READ,
	PERMISSIONS.CAMERAS_READ,
	PERMISSIONS.RUNNER_QUEUE_READ,
	PERMISSIONS.RUNNER_QUEUE_MANAGE,
	PERMISSIONS.SHIFTS_READ,
	PERMISSIONS.SHIFTS_MANAGE,
];

const loggedAsQuery = gql`
	query whoAmI {
		loggedInUser {
			id
			name
			isRootAdmin
			userRoles {
				id
				installationId
				role {
					id
					name
					label
					description
					installationId
					permissions {
						id
						permission
					}
				}
			}
		}
	}
`;

export async function downloadWithAuth({ url, filename = "download.xlsx", method = "GET", body, headers = {} }) {
	try {
		showGlobalLoader();

		const response = await fetch(url, {
			method,
			headers: {
				...getAuthHeaders(),
				...headers,
			},
			body: method === "POST" && body ? JSON.stringify(body) : undefined,
		});

		if (!response.ok) {
			throw new Error(`Download failed with status ${response.status}`);
		}

        if (response.status === 204) {
            alert("No records found for the extras report.");
            return;
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
        console.error("Download failed:", err);
        alert("Download failed. Please try again.");
    } finally {
        hideGlobalLoader();
    }
}

export const PUBLIC_PAGES = ["/login"];

export class AuthManager extends EventEmitter {
	constructor() {
		super();
		this._initialized = false;
		this._isUpdatingAuthState = false;
		this._loggedInUser = null;
		this._willRetryConnectionAt = null;
		this._token = localStorage.getItem("jwt") || null;
		this._statusMessage = "Authenticating";
		this._backTo = sessionStorage?.getItem("backTo");
	}

	initialize() {
		if (this._initialized) return;

		this._initialized = true;
		// Do NOT call updateAuthState without auth token
    for (const publicPage of PUBLIC_PAGES) {
      const regexp = new RegExp(`^${publicPage}$`);
      if (window.location.pathname.match(regexp) && !this._token) return;
    }

		this.updateAuthState();
	}

	get currentUser () {
		return this._loggedInUser;
	}

	get token() {
		return this._token;
	}

	get isAuthenticated() {
		return !!this._loggedInUser;
	}

	get userId() {
		return this._loggedInUser?.id ?? null;
	}

	get userName() {
		return this._loggedInUser?.name ?? null;
	}

	get isUpdating() {
		return this._isUpdatingAuthState;
	}

	get statusMessage() {
		if (this._statusMessage) {
			return this._statusMessage;
		} else if (this._willRetryConnectionAt) {
			const retryInSecs = Math.floor(
				this._willRetryConnectionAt - Date.now() / 1000
			);
			return `Failed to connect to server. Retrying in ${retryInSecs}s`;
		}
		return null;
	}

	currentRoleAt(installationId) {
		if (!this._loggedInUser) return null;
		if (this._loggedInUser.isRootAdmin) return "Root Administrator";

		const userRole = this._loggedInUser.userRoles?.find((userRole) => {
			return userRole.installationId === installationId;
		});

		return userRole?.role.label ?? null;
	}

	isRootAdmin () {
		return this._loggedInUser?.isRootAdmin;
	}

	rolesAt(installationId) {
		if (!this._loggedInUser) return [];

		return (this._loggedInUser.userRoles || [])
			.filter((userRole) => {
				return userRole.installationId === installationId;
			})
			.map((userRole) => userRole.role)
			.filter(Boolean);
	}

	permissionsAt(installationId) {
		const roles = this.rolesAt(installationId);

		return [
			...new Set(
				roles.flatMap((role) => {
					return (role.permissions || []).map((rolePermission) => {
						return rolePermission.permission;
					});
				})
			),
		];
	}

	hasPermission(permission, installationId) {
		if (!this._loggedInUser) return false;
		if (this._loggedInUser.isRootAdmin) return true;

		return this.permissionsAt(installationId).includes(permission);
	}

	hasAnyPermission(permissions, installationId) {
		if (!this._loggedInUser) return false;
		if (this._loggedInUser.isRootAdmin) return true;

		return permissions.some((permission) => {
			return this.hasPermission(permission, installationId);
		});
	}

	hasAllPermissions(permissions, installationId) {
		if (!this._loggedInUser) return false;
		if (this._loggedInUser.isRootAdmin) return true;

		return permissions.every((permission) => {
			return this.hasPermission(permission, installationId);
		});
	}

	canAccess(requiredAccess, installationId) {
		if (!this._loggedInUser) return false;
		if (this._loggedInUser.isRootAdmin) return true;

		if (!requiredAccess) {
			return this.permissionsAt(installationId).length > 0;
		}

		if (typeof requiredAccess === 'string') {
			return this.hasPermission(requiredAccess, installationId);
		}

		const permissions = requiredAccess.permissions || requiredAccess;

		if (!Array.isArray(permissions)) {
			return false;
		}

		if (requiredAccess.mode === 'all') {
			return this.hasAllPermissions(permissions, installationId);
		}

		return this.hasAnyPermission(permissions, installationId);
	}

  landingPathFor(installation) {
    if (this.hasPermission(PERMISSIONS.DASHBOARD_READ, installation.id)) {
      return `/${installation.brandingKey}/dashboard`;
    }

    if (this.hasPermission(PERMISSIONS.RUNNER_QUEUE_MANAGE, installation.id)) {
      return `/${installation.brandingKey}/runner`;
    }

    return `/${installation.brandingKey}/profile`;
  }

	async updateAuthState() {
		if (!this._token) {
			console.error("Can't update auth state - token missed");
			await this.logout();
			return;
		}

		this._isUpdatingAuthState = true;
		try {
			const { data } = await drivoApollo.query({
				query: loggedAsQuery,
				fetchPolicy: "network-only",
			});
			this._loggedInUser = data.loggedInUser;
			this.emit(AUTH_STATE_CHANGE, AUTHENTICATED);
			this.backTo();
		} catch (err) {
			console.error("Error during updateAuthState", err.message);
			await this.logout();
		} finally {
			this._isUpdatingAuthState = false;
		}
	}

	async login({ email, password }) {
		const { data } = await api.login({ email, password, trustedBrowser: localStorage.getItem('trustedBrowser') });
		if (data?.mfaRequired) return data;

		if (data?.user?.token) {
			this._token = data.user.token;
			localStorage.setItem("jwt", this._token);
		}

		await this.updateAuthState();
	}

	async verifyMfa({ challenge, code, trustBrowser }) {
		const { data } = await api.verifyMfa({ challenge, code, trustBrowser });
		if (data.trustedBrowser) localStorage.setItem('trustedBrowser', data.trustedBrowser);
		this._token = data.user.token;
		localStorage.setItem('jwt', this._token);
		await this.updateAuthState();
	}

	async logout() {
		localStorage.removeItem("jwt");
		localStorage.removeItem("installationId");
		this._token = null;
		this._loggedInUser = null;
		this.emit(AUTH_STATE_CHANGE, UN_AUTHENTICATED);
		sessionStorage?.setItem('backTo', window.location.href);

		window.location.href = '/login';
	}

	async backTo() {
		if (!this._backTo) return;

		try {
		const href = new URL(this._backTo, window.location.origin);
		window.location.href = `${href.pathname}${href.search}`;
		} finally {
			this._backTo = null;
			sessionStorage?.removeItem("backTo");
		}
	}
}

const auth = new AuthManager();
auth.initialize();

export default auth;
